/* eslint-disable @typescript-eslint/no-explicit-any */
import { inject, Injectable } from "@angular/core";
import { map, Observable } from "rxjs";
import {
  BatchContainer,
  PaginationContainer,
  PaginationContainerData,
  PaginationItems,
} from "../models/pagination-container.model";
import { HttpClient } from "@angular/common/http";
import { environment } from "src/environments/environment";
import { stripNullishParams } from "../helpers/utils/http-params.util";
import { FilterJourney } from "../models/journeys/filter-journey.model";
import { WorkDayJourney } from "../models/journeys/work-day-journey.model";
import { GetTypeJourney } from "../models/journeys/get-journey-type.model";
import { GetJourney } from "../models/journeys/get-journey.model";
import { GetScheduleTypeJourney } from "../models/journeys/get-journey-schedule-type.model";
import { CreateJourney } from "../models/journeys/create-journey.model";
import { GetJourneyEmployee } from "../models/journeys/get-journey-collaborator";
import { FilterJourneyEmployee } from "../models/journeys/filter-journey-collaborator";
import { JourneyEmployeeCombobox } from "../models/journeys/journey-employee-combobox.model";
import { RequestMoveEmployeeIdNewJourney } from "../models/journeys/move-employee-new-journey.model";
import { CancelEmployeePendingMove } from "../models/journeys/cancel-journey-move";
import { GetJourneyById } from "../models/journeys/get-journey-by-id.model";
import { GetJourneyDay } from "../models/journeys/get-journey-day.model";
import { timeDiff, timeFormat } from "../extensions/time-functions";
import { JourneyDetailWorkDay } from "../models/journeys/journey-detail-work-day.model";
import {
  DocumentItemList,
  PaginationDocument,
  PreviewResult,
  Result,
} from "../models/journeys/documents";
import {
  WorkJourneyDayOffEmployee,
  WorkJourneyDayOffEmployeeResponse,
} from "../models/journeys/work-journey-day-off-employee.model";
import {
  WorkJourneyDayOffDashboardResponse,
  WorkJourneyDayOffDashboardFilter,
} from "../models/journeys/work-journey-day-off-dashboard.model";
import { GetJourneyBySupervisor } from "../models/journeys/get-journey-by-supervisor";
import { Paginations } from "../models/journeys/paginations";
import { GetEmployeeBySupervisor } from "../models/journeys/get-employee-by-supervisor";

@Injectable({
  providedIn: "root",
})
export class JourneyService {
  private http = inject(HttpClient);
  private url = `${environment.url}/v1/work-journey`;
  private timesheetUrl = `${environment.timeSheetManagement.url}`;

  static workDays: WorkDayJourney[] = [
    { day: "Dom", selected: false, name: "Domingo", weekDay: 0 },
    { day: "Seg", selected: false, name: "Segunda-feira", weekDay: 1 },
    { day: "Ter", selected: false, name: "Terça-feira", weekDay: 2 },
    { day: "Qua", selected: false, name: "Quarta-feira", weekDay: 3 },
    { day: "Qui", selected: false, name: "Quinta-feira", weekDay: 4 },
    { day: "Sex", selected: false, name: "Sexta-feira", weekDay: 5 },
    { day: "Sab", selected: false, name: "Sábado", weekDay: 6 },
  ];

  get(filter: FilterJourney): Observable<PaginationContainer<GetJourney>> {
    return this.http.get<PaginationContainer<GetJourney>>(`${this.url}/`, {
      params: { ...filter },
    });
  }

  getById(id: number): Observable<GetJourneyById> {
    return this.http.get<GetJourneyById>(`${this.url}/${id}`);
  }

  getTypes(): Observable<GetTypeJourney[]> {
    return this.http
      .get<BatchContainer<GetTypeJourney>>(`${this.url}/types`)
      .pipe(map((value) => value.content));
  }

  getScheduleTypes(): Observable<GetScheduleTypeJourney[]> {
    return this.http
      .get<BatchContainer<GetScheduleTypeJourney>>(`${this.url}/schedule-types`)
      .pipe(map((value) => value.content));
  }

  create(
    journey: CreateJourney,
  ): Observable<{ message: string; data: number }> {
    return this.http.post<{ message: string; data: number }>(
      `${this.url}`,
      journey,
    );
  }

  delete(id: number): Observable<any> {
    return this.http.delete<any>(`${this.url}/${id}`);
  }

  getEmployees(
    journeyId: number,
    filter: FilterJourneyEmployee,
  ): Observable<PaginationContainer<GetJourneyEmployee>> {
    return this.http.get<PaginationContainer<GetJourneyEmployee>>(
      `${this.url}/${journeyId}/employees`,
      { params: stripNullishParams({ ...filter }) },
    );
  }

  getEmployeeJourney(
    journeyId: number,
    employeeId: number,
  ): Observable<{ isFlexible: boolean }> {
    return this.http.get<{ isFlexible: boolean }>(
      `${this.url}/${journeyId}/employees/${employeeId}`,
    );
  }

  getEmployeesForCombobox(
    journeyId: number,
  ): Observable<JourneyEmployeeCombobox[]> {
    return this.http.get<JourneyEmployeeCombobox[]>(
      `${environment.url}/v1/work-journey/employees-work-journey/${journeyId}`,
    );
  }

  getEmployeesBySupervisor(pagination: Paginations) {
    return this.http.get<PaginationItems<GetEmployeeBySupervisor>>(
      `${this.url}/dashboard-work-journey-by-supervisor`,
      {
        params: stripNullishParams({ ...pagination }),
      },
    );
  }

  moveEmployeesNewJourney(
    currentJourneyId: number,
    items: RequestMoveEmployeeIdNewJourney,
  ) {
    return this.http.put<any>(
      `${this.url}/${currentJourneyId}/employees`,
      items,
    );
  }

  update(id: number, journey: CreateJourney): Observable<void> {
    return this.http.put<void>(`${this.url}/${id}`, journey);
  }

  cancelEmployeePendingMove(
    journeyId: number,
    request: CancelEmployeePendingMove,
  ): Observable<any> {
    return this.http.post<any>(
      `${this.url}/${journeyId}/employees/cancel-changes`,
      request,
    );
  }

  cancelChanges(journeyId: number): Observable<any> {
    return this.http.post<any>(`${this.url}/${journeyId}/cancel-changes`, null);
  }

  getDetailWorkDay(
    journeyDays: GetJourneyDay[],
    weekDay: number,
  ): JourneyDetailWorkDay {
    const day = journeyDays.find(
      (item: { dayOfWeek: number }) => item.dayOfWeek === weekDay,
    );
    if (!day) return;

    const startTime = timeFormat(day.from);
    const endTime = timeFormat(day.to);
    const interval = day.restMinutesDay;
    const estimateHours = timeDiff(day.from, day.to, day.restMinutesDay);

    return { startTime, endTime, interval, estimateHours };
  }

  getNextDaysOff(workJourneyId: number): Observable<Date[]> {
    const timesheetUrl = `${this.timesheetUrl}/work-journey-day-off/next`;
    return this.http
      .get<{ data: { dayOff: string }[] }>(timesheetUrl, {
        params: { WorkJourneyId: workJourneyId.toString() },
      })
      .pipe(
        map((response) =>
          (response?.data ?? []).map((item) => new Date(item.dayOff)),
        ),
      );
  }

  /** Planned Day Offs */
  getWorkJourneysBySupervisor() {
    return this.http.get<GetJourneyBySupervisor[]>(
      `${this.url}/work-journey-by-supervisor`,
    );
  }

  /** Documents */
  getDocuments(
    workJourneyId: number,
    pagination: PaginationDocument,
  ): Observable<PaginationContainerData<DocumentItemList>> {
    return this.http.get<PaginationContainerData<DocumentItemList>>(
      `${this.timesheetUrl}/work-journey-day-off/dashboard/documents`,
      { params: stripNullishParams({ ...pagination, workJourneyId }) },
    );
  }

  getLastDocument(workJourneyId: number) {
    return this.http.post(
      `${this.timesheetUrl}/work-journey-day-off/last-document`,
      {
        workJourneyId: `${workJourneyId}`,
      },
      {
        responseType: "blob",
      },
    );
  }

  importTable(
    workJourneyId: string,
    base64: string,
    fileName: string,
    type: string,
  ) {
    const file = this.base64ToFile(base64, fileName, type);

    const formData = new FormData();
    formData.append("WorkJourneyId", `${workJourneyId}`);
    formData.append("File", file);

    return this.http.post<Result<PreviewResult>>(
      `${this.timesheetUrl}/work-journey-day-off/import`,
      formData,
    );
  }

  private base64ToFile(base64: string, fileName: string, type: string): File {
    const bstr = atob(base64);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([u8arr], fileName, { type });
  }

  /**
   * Importa arquivo com folgas
   * POST /api/v1/work-journey-day-off/import
   * @param file Arquivo para ser importado
   * @param workJourneyId Identificador da Jornada (opcional - quando não passado será utilizado para o Supervisor)
   * @returns Observable com totalImport e totalError
   */
  importDayOffFile(
    file: File,
    workJourneyId?: number | string,
  ): Observable<{ data: { totalImport: number; totalError: number } }> {
    const formData = new FormData();
    formData.append("File", file);

    if (workJourneyId !== null && workJourneyId !== undefined) {
      formData.append("WorkJourneyId", workJourneyId.toString());
    }

    const url = `${this.timesheetUrl}/work-journey-day-off/import`;
    return this.http.post<{
      data: { totalImport: number; totalError: number };
    }>(url, formData, {
      withCredentials: true,
    });
  }

  getEmployeeDayOffs(
    employeeId?: number | string | null,
  ): Observable<WorkJourneyDayOffEmployee[]> {
    const url = `${this.timesheetUrl}/work-journey-day-off/employee`;

    if (employeeId === null) {
      return this.http
        .get<WorkJourneyDayOffEmployeeResponse>(url, {
          params: { EmployeeId: null },
        })
        .pipe(map((response) => response?.data ?? []));
    }

    if (employeeId === undefined) {
      return this.http
        .get<WorkJourneyDayOffEmployeeResponse>(url)
        .pipe(map((response) => response?.data ?? []));
    }

    return this.http
      .get<WorkJourneyDayOffEmployeeResponse>(url, {
        params: stripNullishParams({ EmployeeId: employeeId.toString() }),
      })
      .pipe(map((response) => response?.data ?? []));
  }

  addDayOffEmployees(payload: {
    workJourneyId: number | string | null;
    dayOff: string;
    employeeIds: string[];
  }): Observable<{ data: { totalImport: number; totalError: number } }> {
    const url = `${this.timesheetUrl}/work-journey-day-off/employees`;
    return this.http.post<{
      data: { totalImport: number; totalError: number };
    }>(
      url,
      {
        workJourneyId: payload.workJourneyId,
        dayOff: payload.dayOff,
        employeeIds: payload.employeeIds,
      },
      { withCredentials: true },
    );
  }

  removeDayOffEmployees(payload: {
    workJourneyId: number | string | null;
    dayOff: string;
    employeeIds: (string | number)[];
  }): Observable<any> {
    const url = `${this.timesheetUrl}/work-journey-day-off/remove/employees`;
    const options = {
      body: {
        workJourneyId: payload.workJourneyId,
        dayOff: payload.dayOff,
        employeeIds: payload.employeeIds,
      },
      withCredentials: true,
    };
    return this.http.delete<any>(url, options);
  }

  removeDayOffDay(payload: {
    workJourneyId: number | string | null;
    dayOff: string;
  }): Observable<any> {
    const url = `${this.timesheetUrl}/work-journey-day-off/remove/day`;
    const options = {
      body: {
        workJourneyId: payload.workJourneyId,
        dayOff: payload.dayOff,
      },
      withCredentials: true,
    };
    return this.http.delete<any>(url, options);
  }
}
